import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { opApi } from "@/lib/api";
import { FileText, CalendarDays, ShoppingBag, CreditCard, Loader2, Mail, Phone } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const pdfHref = (u) => (u && String(u).startsWith("http") ? u : API_BASE + u);
const money = (n) => "$" + Number(n || 0).toFixed(2);

const BK_STATUS = {
  requested: { label: "Requested", cls: "bg-amber-500" },
  contract_sent: { label: "Contract sent", cls: "bg-indigo-500" },
  signed: { label: "Signed", cls: "bg-sky-500" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-600" },
  completed: { label: "Completed", cls: "bg-slate-500" },
  cancelled: { label: "Cancelled", cls: "bg-red-500" },
};

export function CustomerDetailDialog({ customer, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loyalty, setLoyalty] = useState([]);

  useEffect(() => {
    if (!open || !customer) return;
    setLoading(true);
    Promise.all([
      opApi.get(`/tenant/customers/${customer.id}/bookings`).then((r) => r.data).catch(() => []),
      opApi.get(`/tenant/customers/${customer.id}/orders`).then((r) => r.data).catch(() => []),
      opApi.get(`/tenant/customers/${customer.id}/loyalty`).then((r) => r.data).catch(() => []),
    ]).then(([b, o, l]) => { setBookings(b || []); setOrders(o || []); setLoyalty(l || []); })
      .finally(() => setLoading(false));
  }, [open, customer]);

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg max-h-[85vh] overflow-y-auto" data-testid="customer-detail-dialog">
        <DialogHeader>
          <DialogTitle>{customer.name}</DialogTitle>
          <DialogDescription className="flex flex-wrap gap-x-4 gap-y-1 text-xs pt-1">
            {customer.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{customer.email}</span>}
            {customer.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{customer.phone}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 mb-2">
          <div className="border border-border p-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Orders</div>
            <div className="font-mono font-bold text-lg">{customer.order_count ?? orders.length}</div>
          </div>
          <div className="border border-border p-3">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Total spent</div>
            <div className="font-mono font-bold text-lg">{money(customer.total_spent)}</div>
          </div>
        </div>

        {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}

        {!loading && (
          <>
            <Section icon={<CalendarDays className="h-4 w-4" />} title={`Event bookings (${bookings.length})`}>
              {bookings.length ? bookings.map((b) => {
                const s = BK_STATUS[b.status] || { label: b.status, cls: "bg-slate-400" };
                return (
                  <div key={b.id} className="border-t border-border py-2.5" data-testid={`cust-booking-${b.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{b.package_name || "Event"}</span>
                      <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 text-white ${s.cls}`}>{s.label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground">{b.date} at {b.time}{b.quote_amount != null ? ` · Quoted ${money(b.quote_amount)}` : ""}</div>
                    {b.contract_pdf_url && (
                      <a href={pdfHref(b.contract_pdf_url)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`cust-booking-contract-${b.id}`}>
                        <FileText className="h-3.5 w-3.5" /> View signed contract
                      </a>
                    )}
                  </div>
                );
              }) : <Empty>No event bookings yet.</Empty>}
            </Section>

            <Section icon={<CreditCard className="h-4 w-4" />} title="Loyalty">
              {loyalty.length ? loyalty.map((m) => (
                <div key={m.id} className="border-t border-border py-2 flex items-center justify-between text-sm" data-testid={`cust-loyalty-${m.id}`}>
                  <span className="font-medium">{m.program_title}</span>
                  <span className="font-mono text-xs">{m.progress}/{m.goal}{m.rewards_ready > 0 ? ` · ${m.rewards_ready} ready` : ""}</span>
                </div>
              )) : <Empty>Not enrolled in any program.</Empty>}
            </Section>

            <Section icon={<ShoppingBag className="h-4 w-4" />} title={`Recent orders (${orders.length})`}>
              {orders.length ? orders.slice(0, 8).map((o) => (
                <div key={o.id} className="border-t border-border py-1.5 flex items-center justify-between text-xs" data-testid={`cust-order-${o.id}`}>
                  <span className="font-mono text-muted-foreground">#{o.order_number}</span>
                  <span className="capitalize">{o.status}</span>
                  <span className="font-mono">{money(o.total)}</span>
                </div>
              )) : <Empty>No orders yet.</Empty>}
            </Section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="mt-3">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground font-medium">{icon}{title}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function Empty({ children }) { return <div className="text-xs text-muted-foreground py-2">{children}</div>; }
